import React, { useCallback, useRef, useState } from 'react';
import {
  Button,
  Edit,
  Filter,
  FormTab,
  TextInput,
  TabbedForm,
  TopToolbar,
  List,
  useEditController,
  useNotify,
  useRefresh
} from 'react-admin';
import { FormSpy } from 'react-final-form';
import { makeStyles, Grid, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import omit from 'lodash/omit';

import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import EditToolbar from '../../core/components/edit/EditToolbar';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import FormTitleBar from '../../core/components/FormTitleBar';
import Empty from '../../core/components/list/Empty';
import { ListPagination } from '../../core/components/list/ListPagination';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import {
  getInitials,
  predictiveSearchFormatter,
  predictiveSearchParser,
  removePathEnd
} from '../../core/utils';

import ViewTab from '../../core/components/forked/layout/ViewTab';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import FundApi from '../../core/apis/FundApi';
import FundTagsAssociationsApi from '../../core/apis/FundTagsAssociationsApi';
import FundInvestorCreateUpdate from '../fundinvestors/FundInvestorCreateUpdate';
import InvestorCreateDialog from './InvestorCreateDialog';
import InvestorFundDatagrid from './InvestorFundDatagrid';
import { validateFund } from './creationSteps/utils';
import FundSummary from './FundSummary';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  }
});

const useStyles = makeStyles({
  headerContainer: {
    paddingTop: '8px'
  }
});

const InvestorFundActionStyles = {
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    marginLeft: '10px'
  }
};

const InvestorFilters = props => (
  <Filter variant="standard" {...props}>
    <TextInput
      source="investorName"
      alwaysOn
      parse={predictiveSearchParser}
      format={predictiveSearchFormatter}
      label="Search by Investor Name"
    />
    <TextInput
      source="investorId"
      alwaysOn
      parse={predictiveSearchParser}
      format={predictiveSearchFormatter}
      label="Search by Investor ID"
    />
  </Filter>
);

const InvestorFundAction = withStyles(InvestorFundActionStyles)(
  ({
    classes,
    hasCreateInvestorPermission,
    hasWriteFundInvestorPermission,
    ...props
  }) => (
    <TopToolbar>
      {hasCreateInvestorPermission && hasWriteFundInvestorPermission && (
        <Button
          color="primary"
          onClick={props.handleInvestorCreateButtonClick}
          label="Add New Investor"
          className={classes.addButton}
          disableFocusRipple
        />
      )}
      {hasWriteFundInvestorPermission && (
        <Button
          color="primary"
          onClick={props.handleFundInvestorCreateButtonClick}
          label="Add Existing Investor"
          className={classes.addButton}
          disableFocusRipple
        />
      )}
    </TopToolbar>
  )
);

const FundTitle = ({ record }) => {
  return (
    <FormTitleBar
      recordLabel={'Fund ID: ' + record.id}
      name={record.name}
      status={record.status}
      statusLabel={'Status:'}
      showAvatar={true}
      avatarContent={getInitials(record.name)}
    />
  );
};

const FundRelatedInvestorsLabel = ({ label }) => {
  const classes = useStyles();

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.headerContainer}>
          {label}
        </Typography>
      </Grid>
    </Grid>
  );
};

const FundEdit = props => {
  const {
    classes,
    permissions = [],
    hasReadPermission,
    hasEditPermission,
    hasCreatePermission,
    hasDeletePermission,
    ...rest
  } = props;

  const [fundInvestorRecord, setFundInvestorRecord] = useState({});
  const [fundInvestorBasePath, setFundInvestorBasePath] = useState({});
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);
  const [hasErrorOnSubmit, setHasErrorOnSubmit] = useState(false);
  const hasModificationSubmittedRef = useRef(false);

  const [showInvestorCreateDialog, setShowInvestorCreateDialog] = useState(
    false
  );
  const [showFundInvestorEditDialog, setShowFundInvestorEditDialog] = useState(
    false
  );
  const [
    showFundInvestorCreateDialog,
    setShowFundInvestorCreateDialog
  ] = useState(false);

  const { record } = useEditController(props);
  const notifyServerError = useNotifyServerError();
  const notify = useNotify();
  const refresh = useRefresh();

  const fundId = props.id;
  const displayToolbar = props.match.isExact;

  const hasCreateInvestorPermission = permissions.includes(
    'AUTH_CREATE_INVESTOR'
  );
  const hasWriteFundInvestorPermission = permissions.includes(
    'AUTH_WRITE_FUND_INVESTOR'
  );
  const hasDeleteFundInvestorPermission = permissions.includes(
    'AUTH_DELETE_FUND_INVESTOR'
  );
  const hasEditFundTagPermission = permissions.includes('AUTH_EDIT_FUND_TAG');

  const onBeforeDelete = () => setWhenBlockNavigation(false);
  const handleInvestorFundEditButtonClick = (fiRecord, fiBasePath) => {
    setShowFundInvestorEditDialog(prev => !prev);
    setFundInvestorRecord(fiRecord);
    setFundInvestorBasePath(fiBasePath);
  };
  const handleInvestorCreateDialogToggle = () =>
    setShowInvestorCreateDialog(prev => !prev);
  const handleFundInvestorEditDialogToggle = () =>
    setShowFundInvestorEditDialog(prev => !prev);
  const handleFundInvestorCreateButtonClick = () =>
    setShowFundInvestorCreateDialog(prev => !prev);
  const handleFundInvestorCreateDialogToggle = () =>
    setShowFundInvestorCreateDialog(prev => !prev);

  const onSubmitEnd = useCallback(() => {
    if (!hasErrorOnSubmit && hasModificationSubmittedRef) {
      notify('ra.notification.custom_updated_message');
      hasModificationSubmittedRef.current = false;
    }
    refresh();
  }, [hasErrorOnSubmit, notify, refresh]);

  const handleSubmit = useCallback(
    async values => {
      const recordStringify = JSON.stringify(omit(record, 'fundTags'));
      const valuesStringify = JSON.stringify(omit(values, 'fundTags'));
      const tagAssociationsToAdd = Object.keys(values.fundTags)
        .filter(tagId => !Object.keys(record.fundTags).includes(tagId))
        .map(fundTagId => ({ fundId, fundTagId }));

      let tagAssociationsToRemove = [];
      Object.entries(values.fundTags).forEach(([fundTagId, name]) => {
        if (name === null) {
          tagAssociationsToRemove.push({
            fundId,
            fundTagId
          });
        }
      });

      setWhenBlockNavigation(false);

      try {
        if (recordStringify !== valuesStringify) {
          hasModificationSubmittedRef.current = true;
          await FundApi.edit(omit(values, 'fundTags'), record.id);
        }
        if (tagAssociationsToAdd.length > 0) {
          hasModificationSubmittedRef.current = true;
          await FundTagsAssociationsApi.create(tagAssociationsToAdd);
        }
        if (tagAssociationsToRemove.length > 0) {
          hasModificationSubmittedRef.current = true;
          await FundTagsAssociationsApi.delete(tagAssociationsToRemove);
        }
      } catch (err) {
        setHasErrorOnSubmit(true);
        notifyServerError(err, undefined);
      } finally {
        onSubmitEnd();
      }
    },
    [fundId, notifyServerError, onSubmitEnd, record]
  );

  return (
    <React.Fragment>
      <NavigationPrompt
        when={whenBlockNavigation}
        adjustMatchingPath={removePathEnd}
        hasEditPermission={hasEditPermission}
      >
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onConfirm={onConfirm}
            onClose={onCancel}
          />
        )}
      </NavigationPrompt>

      <Edit
        {...omit(rest, ['userProfile'])}
        undoable={false}
        title={<FundTitle />}
        classes={{ noActions: classes.noActions }}
      >
        <TabbedForm
          save={handleSubmit}
          initialValues={props.record}
          toolbar={
            displayToolbar ? (
              <EditToolbar
                {...rest}
                hasEditPermission={hasEditPermission}
                hasDeletePermission={hasDeletePermission}
                onBeforeDelete={onBeforeDelete}
              />
            ) : (
              <></>
            )
          }
          variant="standard"
          margin="normal"
          validate={validateFund}
        >
          <FormTab label="Fund Details">
            <FundSummary hasEditFundTagPermission={hasEditFundTagPermission} />
            <FormSpy
              subscription={{ dirty: true }}
              onChange={e => {
                setWhenBlockNavigation(e.dirty);
              }}
            />
          </FormTab>
          <ViewTab label="Related Investors">
            <FundRelatedInvestorsLabel label="Related Investors" />
            <List
              style={{ margin: '0px', width: '100%' }}
              filters={<InvestorFilters />}
              pagination={<ListPagination />}
              basePath="/fundinvestors"
              resource="fundinvestors"
              filter={{ fundId }}
              sort={{ field: 'investorName', order: 'ASC' }}
              title=" "
              actions={
                <InvestorFundAction
                  fundId={fundId}
                  handleInvestorCreateButtonClick={
                    handleInvestorCreateDialogToggle
                  }
                  handleFundInvestorCreateButtonClick={
                    handleFundInvestorCreateButtonClick
                  }
                  hasCreateInvestorPermission={hasCreateInvestorPermission}
                  hasWriteFundInvestorPermission={
                    hasWriteFundInvestorPermission
                  }
                />
              }
              bulkActionButtons={
                hasDeleteFundInvestorPermission && (
                  <BulkActionDeleteButton confirmContent="Any wire instructions linked to the fund-investor relationship will be removed with this action. Are you sure you want to proceed ?" />
                )
              }
              empty={
                <Empty
                  emptyText="There are no related Investors to be displayed"
                  buttons={[
                    {
                      label: 'Add New Investor',
                      onClick: handleInvestorCreateDialogToggle,
                      hide: !(
                        hasCreateInvestorPermission &&
                        hasWriteFundInvestorPermission
                      )
                    },
                    {
                      label: 'Add Existing Investor',
                      onClick: handleFundInvestorCreateButtonClick,
                      hide: !hasWriteFundInvestorPermission
                    }
                  ]}
                />
              }
            >
              <InvestorFundDatagrid
                handleInvestorFundEditButtonClick={
                  handleInvestorFundEditButtonClick
                }
                permissions={permissions}
                {...rest}
              />
            </List>
            <FundInvestorCreateUpdate
              operation="Update"
              title=" "
              showDialog={showFundInvestorEditDialog}
              handleDialogToggle={handleFundInvestorEditDialogToggle}
              {...rest}
              fundId={fundId}
              id={fundInvestorRecord.id}
              basePath={fundInvestorBasePath}
              resource={fundInvestorRecord}
            />
            <InvestorCreateDialog
              title=" "
              showDialog={showInvestorCreateDialog}
              handleDialogToggle={handleInvestorCreateDialogToggle}
              {...rest}
            />
            <FundInvestorCreateUpdate
              operation="Create"
              title=" "
              showDialog={showFundInvestorCreateDialog}
              handleDialogToggle={handleFundInvestorCreateDialogToggle}
              {...rest}
              fundId={fundId}
            />
          </ViewTab>
        </TabbedForm>
      </Edit>
    </React.Fragment>
  );
};

export default withStyles(styles)(FundEdit);
